<script>
  // The transform palette as an ordered chain of steps rather than a JSON blob.
  // Each step is one card with controls for exactly its own arguments; a step
  // after the first runs over whatever the previous one produced, which is how a
  // `coarsen` column earns a real coarsening (split → match values).
  import Alert from './Alert.svelte';
  import Button from './Button.svelte';
  import MapValuesEditor from './MapValuesEditor.svelte';
  import { valueSchemeFor } from '../../../lib/mapping_workbench.js';
  import { TRANSFORM_LABELS, transformSteps, collapseTransform, validateTransform, transformWarnings }
    from '../../../lib/disclosure_core.js';

  let { column, catalog = null, rows = [], headers = [], onchange = () => {} } = $props();

  const valueScheme = $derived(valueSchemeFor(catalog, column.term));

  const PALETTE = ['direct', 'mapValues', 'split', 'dateBucket', 'bucket', 'rules', 'const'];
  const DEFAULTS = {
    direct: () => ({ fn: 'direct' }),
    mapValues: () => ({ fn: 'mapValues', map: {}, default: 'drop' }),
    split: () => ({ fn: 'split', delimiter: ',' }),
    dateBucket: () => ({ fn: 'dateBucket', granularity: 'quarter' }),
    bucket: () => ({ fn: 'bucket', parse: 'number', edges: [10, 50], labels: ['low', 'medium', 'high'] }),
    rules: () => ({ fn: 'rules', rules: [{ when: [{ col: '@self', op: 'eq', value: '' }], then: '' }], fallback: '' }),
    const: () => ({ fn: 'const', value: '' }),
  };

  const steps = $derived(transformSteps(column.transform));
  const errors = $derived(validateTransform(column.transform));
  const warnings = $derived(transformWarnings(column.transform));

  // Every edit writes the whole chain back, collapsing to the bare-object form
  // while it is a single step so the saved JSON reads as it always has.
  function commit(next) {
    column.transform = collapseTransform(next);
    onchange();
  }
  const replace = (i, step) => commit(steps.map((s, j) => (j === i ? step : s)));
  const edit = (i, patch) => replace(i, { ...steps[i], ...patch });

  function addStep() {
    commit([...steps, DEFAULTS.mapValues()]);
  }
  function removeStep(i) {
    commit(steps.filter((_, j) => j !== i));
  }
  function move(i, by) {
    const next = [...steps];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    commit(next);
  }
  function setFn(i, fn) {
    if (fn === steps[i].fn) return;
    replace(i, DEFAULTS[fn]());
  }

  const numbers = (text) => text.split(',').map((part) => Number(part.trim())).filter((n) => Number.isFinite(n));
  const strings = (text) => text.split(',').map((part) => part.trim()).filter(Boolean);

  function setRule(i, at, patch) {
    edit(i, { rules: steps[i].rules.map((rule, j) => (j === at ? { ...rule, ...patch } : rule)) });
  }
  function setCondition(i, at, patch) {
    setRule(i, at, { when: [{ ...steps[i].rules[at].when[0], ...patch }] });
  }
  function addRule(i) {
    edit(i, { rules: [...steps[i].rules, { when: [{ col: '@self', op: 'eq', value: '' }], then: '' }] });
  }
  function removeRule(i, at) {
    edit(i, { rules: steps[i].rules.filter((_, j) => j !== at) });
  }
</script>

{#each steps as step, i (`${column.source}#${i}#${step.fn}`)}
  <div class="step-card">
    <div class="step-head">
      <span class="step-index" aria-hidden="true">{i + 1}</span>
      <label class="step-name">
        <span class="wf2-caption">Step {i + 1}</span>
        <select
          class="wf2-control"
          aria-label="Step {i + 1} transform"
          value={step.fn}
          onchange={(e) => setFn(i, e.currentTarget.value)}
        >
          {#each PALETTE as fn}
            <option value={fn} disabled={fn === 'const' && i > 0}>{TRANSFORM_LABELS[fn]}</option>
          {/each}
        </select>
      </label>
      <button class="step-icon" aria-label="Move step {i + 1} earlier" disabled={i === 0} onclick={() => move(i, -1)}>↑</button>
      <button class="step-icon" aria-label="Move step {i + 1} later" disabled={i === steps.length - 1} onclick={() => move(i, 1)}>↓</button>
      <button class="step-icon" aria-label="Remove step {i + 1}" disabled={steps.length === 1} onclick={() => removeStep(i)}>×</button>
    </div>

    <div class="step-body">
      {#if step.fn === 'direct'}
        <p class="wf2-caption">The value is published as it stands, with surrounding spaces trimmed. Empty values are not published.</p>

      {:else if step.fn === 'const'}
        <label class="wf2-field">Always publish
          <input class="wf2-control" value={step.value ?? ''} oninput={(e) => edit(i, { value: e.currentTarget.value })} />
        </label>
        <p class="wf2-caption">Ignores the source column entirely — used for the institution's own name and other fixed facts.</p>

      {:else if step.fn === 'split'}
        <label class="wf2-field">Split on
          <input class="wf2-control mono" size="4" value={step.delimiter ?? ''} oninput={(e) => edit(i, { delimiter: e.currentTarget.value })} />
        </label>
        <p class="wf2-caption">One value in, several out. Every later step runs once per value.</p>

      {:else if step.fn === 'dateBucket'}
        <label class="wf2-field">Group by
          <select class="wf2-control" value={step.granularity} onchange={(e) => edit(i, { granularity: e.currentTarget.value })}>
            <option value="quarter">Quarter — 2025-Q1</option>
            <option value="year">Year — 2025</option>
          </select>
        </label>

      {:else if step.fn === 'bucket'}
        <label class="wf2-field">Read the value as
          <select class="wf2-control" value={step.parse ?? 'number'} onchange={(e) => edit(i, { parse: e.currentTarget.value })}>
            <option value="number">A number</option>
            <option value="duration">A duration — "1 hour 30 mins"</option>
          </select>
        </label>
        <label class="wf2-field">Band edges
          <input class="wf2-control mono" value={(step.edges ?? []).join(', ')} onchange={(e) => edit(i, { edges: numbers(e.currentTarget.value) })} />
        </label>
        <label class="wf2-field">Band names
          <input class="wf2-control" value={(step.labels ?? []).join(', ')} onchange={(e) => edit(i, { labels: strings(e.currentTarget.value) })} />
        </label>
        <p class="wf2-caption">One more name than edges: {(step.edges ?? []).length} edges need {(step.edges ?? []).length + 1} names.</p>

      {:else if step.fn === 'mapValues'}
        <MapValuesEditor {step} {column} {rows} scheme={valueScheme} onedit={(patch) => edit(i, patch)} />
        <label class="wf2-field">Anything else
          <input class="wf2-control" value={step.default ?? 'drop'} onchange={(e) => edit(i, { default: e.currentTarget.value })} />
        </label>
        <p class="wf2-caption">Write <span class="mono">drop</span> as the fallback to publish nothing for unlisted values.</p>
        <label class="wf2-checkline"><input type="checkbox" class="wf2-checkbox" checked={Boolean(step.caseInsensitive)} onchange={(e) => edit(i, { caseInsensitive: e.currentTarget.checked })} /> Ignore capitalisation when matching</label>
        <label class="wf2-checkline"><input type="checkbox" class="wf2-checkbox" checked={Boolean(step.dropEmpty)} onchange={(e) => edit(i, { dropEmpty: e.currentTarget.checked })} /> Publish nothing when the cell is empty</label>
        {#if valueScheme}
          <p class="wf2-caption">Published using <span class="mono">{valueScheme.id}</span>. Choosing a concept also sets its {valueScheme.output === 'iri' ? 'term' : 'text'} representation.</p>
        {:else}
          <label class="wf2-field">Publish as
            <select class="wf2-control" value={step.output ?? 'literal'} onchange={(e) => edit(i, { output: e.currentTarget.value })}>
              <option value="literal">Text</option>
              <option value="iri">A term from a value scheme</option>
              <option value="typed">A typed value</option>
            </select>
          </label>
        {/if}
        {#if step.output === 'typed'}
          <label class="wf2-field">Type
            <input class="wf2-control mono" value={step.datatype ?? ''} placeholder="xsd:boolean" onchange={(e) => edit(i, { datatype: e.currentTarget.value })} />
          </label>
        {/if}

      {:else if step.fn === 'rules'}
        <p class="wf2-caption">The first rule that matches wins. <span class="mono">this value</span> is what reaches this step; other columns are read from the original row.</p>
        {#each step.rules ?? [] as rule, at}
          <div class="wf2-row" style="gap:8px; align-items:flex-end">
            <label class="wf2-field" style="min-width:150px">When
              <select class="wf2-control" value={rule.when[0]?.col} onchange={(e) => setCondition(i, at, { col: e.currentTarget.value })}>
                <option value="@self">this value</option>
                {#each headers as header}<option value={header}>{header}</option>{/each}
              </select>
            </label>
            <label class="wf2-field" style="min-width:90px">is
              <select class="wf2-control" value={rule.when[0]?.op} onchange={(e) => setCondition(i, at, { op: e.currentTarget.value })}>
                <option value="eq">equal to</option>
                <option value="neq">not equal to</option>
              </select>
            </label>
            <label class="wf2-field" style="min-width:120px">the value
              <input class="wf2-control" value={rule.when[0]?.value ?? ''} onchange={(e) => setCondition(i, at, { value: e.currentTarget.value })} />
            </label>
            <label class="wf2-field" style="min-width:120px">publish
              <input class="wf2-control" value={rule.then ?? ''} onchange={(e) => setRule(i, at, { then: e.currentTarget.value })} />
            </label>
            <button class="step-icon" aria-label="Remove rule {at + 1}" onclick={() => removeRule(i, at)}>×</button>
          </div>
        {/each}
        <div class="wf2-row" style="gap:12px; align-items:flex-end">
          <Button variant="ghost" onclick={() => addRule(i)}>Add a rule</Button>
          <label class="wf2-field">If no rule matches
            <input class="wf2-control" value={step.fallback ?? ''} onchange={(e) => edit(i, { fallback: e.currentTarget.value })} />
          </label>
        </div>
      {/if}
    </div>
  </div>
{/each}

<Button variant="ghost" onclick={addStep}>Add a step</Button>

{#if errors.length}
  <Alert title="This transform is not finished" variant="danger">
    <ul>{#each errors as error}<li>{error}</li>{/each}</ul>
  </Alert>
{/if}
{#each warnings as warning}
  <Alert title="Check this chain" variant="warning"><p>{warning}</p></Alert>
{/each}
